import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { RESPONSE } from '../store/actionTypes';
import {
  Report,
  Response,
  SubmitResponseInput,
  UpdateResponseInput,
  Pagination,
} from './models/response';

export const response = createApi({
  reducerPath: RESPONSE,
  baseQuery,
  tagTypes: ['Response'],
  endpoints: builder => ({
    submitResponse: builder.mutation<Response, SubmitResponseInput>({
      query: variables => ({
        document: gql`
          mutation SubmitResponse($responseInput: ResponseInput!) {
            submitResponse(responseInput: $responseInput) {
              id
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.submitResponse,
    }),
    updateResponse: builder.mutation<Response, UpdateResponseInput>({
      query: variables => ({
        document: gql`
          mutation UpdateResponse($responseId: ObjectID!, $responseInput: ResponseInput!) {
            updateResponse(responseId: $responseId, responseInput: $responseInput) {
              id
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: any) => response?.updateResponse,
    }),
    saveResponseToCache: builder.mutation<
      Report[][],
      { userId: string; responseCacheInput: Report[][] }
    >({
      query: variables => ({
        document: gql`
          mutation SaveResponseToCache($userId: String, $responseCacheInput: [[ReportInput]]!) {
            saveResponseToCache(userId: $userId, responseCacheInput: $responseCacheInput) {
              value {
                name
                value
              }
              label
              position
              question
            }
          }
        `,
        variables,
      }),
      invalidatesTags: ['Response'],
      transformResponse: (response: any) => response?.saveResponseToCache,
    }),
    getResponseFromCache: builder.query<[[Report]], { userId: string }>({
      query: variables => ({
        document: gql`
          query GetResponseFromCache($userId: String) {
            getResponseFromCache(userId: $userId) {
              value {
                name
                value
              }
              label
              position
              question
            }
          }
        `,
        variables,
      }),
      providesTags: ['Response'],
      transformResponse: (response: any) => response?.getResponseFromCache,
    }),
    resetResponseCache: builder.mutation<[[Report]], { userId: string }>({
      query: variables => ({
        document: gql`
          mutation ResetResponseCache($userId: String) {
            resetResponseCache(userId: $userId) {
              value {
                name
                value
              }
              label
              position
              question
            }
          }
        `,
        variables,
      }),
      invalidatesTags: ['Response'],
      transformResponse: (response: any) => response?.resetResponseCache,
    }),
    getResponsesByFormName: builder.query<
      { data: Response[]; pagination: Pagination },
      { name: string; page: number; limit: number; offset: number }
    >({
      query: variables => ({
        document: gql`
          query GetResponsesByFormName($name: String!, $page: Int, $limit: Int, $offset: Int) {
            getResponsesByFormName(name: $name, page: $page, limit: $limit, offset: $offset) {
              data {
                id
                meta {
                  created
                }
                data
                status
                report {
                  value
                  label
                  position
                  question
                }
                reporter
                user {
                  preferences {
                    notifications {
                      medium
                      status
                    }
                  }
                }
              }
              pagination {
                items
                pages
              }
            }
          }
        `,
        variables,
      }),
      providesTags: ['Response'],
      transformResponse: (response: any) => response?.getResponsesByFormName,
    }),
  }),
});

export const {
  useSubmitResponseMutation,
  useUpdateResponseMutation,
  useSaveResponseToCacheMutation,
  useGetResponseFromCacheQuery,
  useLazyGetResponseFromCacheQuery,
  useResetResponseCacheMutation,
  useGetResponsesByFormNameQuery,
  useLazyGetResponsesByFormNameQuery,
} = response;
